// ============================================================
// TMR Portal Admin — o link do convite, para entrega manual (06 §6.1)
// ============================================================
import type { MouseEvent } from 'react';
import { Btn } from '../../components/primitives';
import { CopyableText } from '../../components/CopyableText';
import { Notice } from '../../components/Notice';
import { Icons, StrokeIcon } from '../../lib/icons';
import type { AccessRequestInviteLink } from '../../data/authorization/contract';
import { ErrorBanner } from './ErrorBanner';
import { OverlayPortal } from '../../components/OverlayPortal';

/** Props for {@link InviteLinkDialog}. */
export interface InviteLinkDialogProps {
  /** Whose invitation this is — the name the operator recognizes, which the payload does not carry. */
  personName: string;
  /** The fetched link; absent while it is still being fetched or if it failed. */
  link?: AccessRequestInviteLink;
  /** True while the backend is being asked. */
  loading: boolean;
  /** Why it could not be produced, when that happened. */
  error?: unknown;
  onClose: () => void;
}

/**
 * The Clerk accept link for one approved person, ready to send through another channel.
 *
 * This is the manual escape hatch for the one hop in the invitation flow that the portal cannot see. Clerk
 * reports the invitation as sent, its dashboard shows it pending, and our row says "Enviado" — and the
 * message can still be dropped by the sending provider or held in the recipient's quarantine. None of
 * those are distinguishable from here, and none are fixable from here either, so the operator gets the
 * link and picks a channel that works.
 *
 * The link is shown rather than copied straight to the clipboard on the row's click, for two reasons: the
 * operator should see who it is for before sending a credential, and a clipboard write that follows an
 * await loses its user activation in some browsers — the copy has to hang off a gesture of its own.
 */
export function InviteLinkDialog({ personName, link, loading, error, onClose }: InviteLinkDialogProps) {
  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <OverlayPortal>
      <div
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={`Convite de ${personName}`}
        style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      >
        <div onClick={stop} style={{ width: 560, maxWidth: '100%', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
          <div style={{ padding: '24px 24px 8px', display: 'flex', gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--sage-100)', color: 'var(--forest-800)' }}>
              <StrokeIcon d={Icons.mail} size={18} strokeWidth={2} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 17, margin: 0, letterSpacing: '-0.01em' }}>
                Convite de {personName}
              </h3>
              <div style={{ fontSize: 13.5, color: 'var(--fg-muted)', marginTop: 6, lineHeight: 1.5 }}>
                Envie este link para a pessoa por outro canal. Ele abre o mesmo cadastro que o e-mail do
                convite abriria.
              </div>

              {loading && (
                <div style={{ marginTop: 16, fontSize: 13, color: 'var(--fg-muted)' }} role="status">
                  Buscando o link no Clerk…
                </div>
              )}

              {!loading && error != null && <ErrorBanner error={error} />}

              {!loading && link && (
                <>
                  <div style={{ marginTop: 16, fontSize: 12, color: 'var(--fg-subtle)' }}>Para</div>
                  <div style={{ fontSize: 13.5, fontWeight: 500 }}>{link.email}</div>

                  <div style={{ marginTop: 14, fontSize: 12, color: 'var(--fg-subtle)' }}>Link</div>
                  {/* `overflowWrap` e não truncagem: um link cortado que parece inteiro é pior do que um
                      link feio — o operador copia pelo botão, mas confere com os olhos. */}
                  <CopyableText
                    value={link.url}
                    style={{ fontSize: 12, fontFamily: 'var(--font-mono, monospace)', overflowWrap: 'anywhere', display: 'inline-block' }}
                  />

                  <Notice tone="warning">
                    Trate como senha: quem abrir o link conclui o cadastro no lugar da pessoa. Mande direto
                    para <strong>{link.email}</strong>, nunca em grupo.
                  </Notice>
                </>
              )}
            </div>
          </div>

          <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn variant="secondary" onClick={onClose}>Fechar</Btn>
            {link && (
              // O clique de copiar nasce aqui, e não do clique que abriu o diálogo: uma escrita na área de
              // transferência depois de um await perde a ativação do usuário em parte dos navegadores.
              <Btn variant="primary" icon={Icons.copy} onClick={() => void navigator.clipboard.writeText(link.url)}>
                Copiar link
              </Btn>
            )}
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
